<template>
    <div id="welcome-messages">
        <supsis-table
            :columns="cols"
            :rows="getFilteredRows()"
            :bottom-pagination="true"
            :pagination-cursor="paginationCursor"
            @cursor-changed="(cursor) => (paginationCursor = cursor)"
            :options="tableOptions"
        >
            <template #header>
                <div class="d-flex justify-content-between align-items-center">
                    <a-input-search
                        v-model="searchText"
                        :placeholder="$t('message.search')"
                        class="search-input"
                        @search="onSearch"
                    />
                    <supsis-button
                        primary
                        @click="showModalWithoutEdit"
                        v-if="checkPermission(PERMISSION_TYPES['welcome.create'].tr.name)"
                    >
                        <md-icon style="color: white">add</md-icon>
                        {{ $t("message.newWelcomeMessages") }}
                    </supsis-button>
                </div>
            </template>
        </supsis-table>

        <Modal2
            v-if="isModalShow"
            id="welcome-message-form"
            :title="editMode ? $t('message.updateWelcomeMessageForm') : $t('message.newWelcomeMessageForm')"
            centered
            hide-footer
            size="lg"
            @cancel="hideModal"
            :show-footer="false"
        >
            <a-alert
                v-if="$validator.errors.items.length && submitClicked"
                :message="$t('message.error')"
                class="mb-2"
                showIcon
                type="error"
            >
                <div slot="description">
                    <div v-for="(err, index) in $validator.errors.items" :key="'err-' + index">
                        <span>
                            {{ err.msg }}
                        </span>
                    </div>
                </div>
            </a-alert>
            <b-form class="d-flex flex-column justify-content-center" @submit.prevent="handleSave">
                <b-row>
                    <b-col md="12">
                        <b-form-group :label="$t('message.title')" label-for="display-name-input">
                            <b-form-select
                                :disabled="editMode"
                                v-model="selectedWMType"
                                v-validate="'required'"
                                :name="$t('message.title')"
                                :options="subjects()"
                            >
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col md="12">
                        <b-row>
                            <b-col md="12">
                                <MultiContent v-model="multiContentComp" ref="MultiContent" />
                            </b-col>
                            <b-col md="12" id="buttons">
                                <b-button @click="hideModal">
                                    <template>
                                        {{ $t("message.cancel") }}
                                    </template>
                                </b-button>
                                <b-button
                                    :disabled="loading"
                                    type="submit"
                                    variant="primary"
                                    v-if="checkPermission(PERMISSION_TYPES['welcome.update'].tr.name)"
                                >
                                    <template v-if="!loading">
                                        <span v-if="!editMode">
                                            {{ $t("message.save") }}
                                        </span>
                                        <span v-else>
                                            {{ $t("message.update") }}
                                        </span>
                                    </template>
                                    <template v-else>
                                        <font-awesome-icon icon="spinner" spin />
                                    </template>
                                </b-button>
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>
            </b-form>
        </Modal2>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faPencilAlt, faPlusCircle, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import AppBreadCrumb from "../../../../../components/common/AppBreadCrumb.vue";
import * as _ from "lodash";
import { log2 } from "../../../../../utils/log2";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import ActionDelete from "../../../../../components/svg/ActionDelete.vue";
import ActionEdit from "../../../../../components/svg/ActionEdit.vue";
import Pagination from "../../../../../components/Pagination.vue";
import MultiContent from "../../../../../components/MultiContent.vue";
import { toObject2 } from "../../../../../utils/languageContent";
import Modal2 from "../../../../../components/common/Modal2.vue";
import { PERMISSION_TYPES } from "../../../../../language/permissionTypes";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../components/SupsisButton.vue";

library.add(faPencilAlt, faTimes, faPlusCircle, faSpinner);

const welcomeMessageInitialObject = {
    _id: undefined,
    languageContent: {},
};
const log = log2("WelcomeMessages");
export default {
    name: "WelcomeMessages",
    components: {
        SupsisButton,
        SupsisTable,
        MultiContent,
        AppBreadCrumb,
        ActionDelete,
        ActionEdit,
        Pagination,
        Modal2,
    },
    data() {
        return {
            isModalShow: false,
            isBusy: true,
            loading: false,
            editMode: false,
            submitClicked: false,
            selectedWMType: null,
            subjects: () => [
                { text: this.$t("message.makeChoice") },
                { value: "welcome", text: this.$t("message.welcomeMessage") },
                { value: "login", text: this.$t("message.repLoggedIn") },
                { value: "byebye", text: this.$t("message.loginMessage") },
            ],
            welcomeMessage: _.cloneDeep(welcomeMessageInitialObject),
            pageSizes: ["10", "25", "50", "100"],
            searchText: undefined,
            selectedLang: this.$i18n.locale,
            cols: [
                {
                    field: "languageContent." + this.$i18n.locale + ".name",
                    key: "languageContent." + this.$i18n.locale + ".name",
                    title: this.$t("message.title"),
                    renderBodyCell: ({ row }) => {
                        return this.subjects().find(
                            (s) => s.value === this.read(row, "languageContent." + this.$i18n.locale + ".name")
                        ).text;
                    },
                },
                {
                    field: "languageContent." + this.$i18n.locale + ".content",
                    key: "languageContent." + this.$i18n.locale + ".content",
                    title: this.$t("message.message"),
                    renderBodyCell: ({ row }) => {
                        const data = _.get(row, "languageContent." + this.$i18n.locale + ".content");
                        return this.domDecoder(data, row);
                    },
                },
                {
                    align: "right",
                    field: "actionsSlot",
                    key: "actionsSlot",
                    title: this.$t("message.transactions"),
                    fixed: "right",
                    copyDisabled: true,
                    renderBodyCell: ({ row }) => {
                        const renderEditButton = () => {
                            return (
                                <a-button
                                    content={this.$t("message.editThisItem")}
                                    class="actionButton d-flex align-items-center"
                                    on-click={() => this.edit(row._id)}
                                >
                                    <span style="margin-right: 16px; display: block">{this.$t("message.edit")}</span>
                                    <ActionEdit />
                                </a-button>
                            );
                        };

                        const renderDeleteButton = () => {
                            return (
                                this.checkPermission(PERMISSION_TYPES["welcome.delete"].tr.name) && (
                                    <a-button
                                        content={this.$t("message.removeThisItem")}
                                        class="actionButton d-flex align-items-center"
                                        on-click={() => this.remove(row._id)}
                                    >
                                        <span style="margin-right: 16px; display: block">
                                            {this.$t("message.deleteField")}
                                        </span>
                                        <ActionDelete />
                                    </a-button>
                                )
                            );
                        };

                        return (
                            <div class="d-flex w-100 justify-content-end">
                                {renderEditButton()}
                                {renderDeleteButton()}
                            </div>
                        );
                    },
                },
            ],
            paginationCursor: {
                total: 0,
                page: 1,
                perPage: 10,
            },
            queryAction: this.$route.query.action,
            tableOptions: {
                eventCustomOption: {
                    bodyCellEvents: () => {
                        return {
                            dblclick: (event) => {
                                const columnIndex = event.target.cellIndex;
                                const column = this.cols[columnIndex];
                                if (!column || column.copyDisabled) {
                                    return;
                                }

                                let text = event.toElement.innerText;
                                let textArea = document.createElement("textarea");
                                textArea.value = text;
                                document.body.appendChild(textArea);
                                textArea.select();
                                document.execCommand("copy");
                                document.body.removeChild(textArea);
                                this.$message.success({
                                    content: this.$t("message.copiedClipboard"),
                                    duration: 2,
                                    key: "saved",
                                });
                            },
                        };
                    },
                },
            },
        };
    },
    computed: {
        PERMISSION_TYPES() {
            return PERMISSION_TYPES;
        },
        ...mapState({
            welcomeMessages: (state) => state.welcomeMessage.welcomeMessages,
            languages: (state) => state.language.languages,
            site: (state) => state.site.site,
            defaultLang: (state) => state.setting.settings.language.clientDefault,
            isAdmin: (state) => state.auth.adminUser != null,
            systemLanguages: (state) => state.setting.settings.systemLanguages,
        }),
        ...mapGetters({
            undeleted: "welcomeMessage/getUndeleted",
            isDarkMode: "user/isDarkMode",
            checkPermission: "auth/checkPermission",
        }),
        multiContentComp: {
            get() {
                if (!this.systemLanguages) {
                    return [];
                }
                return [
                    {
                        type: "rich",
                        field: "content",
                        label: this.$t("message.message"),
                        placeholder: this.$t("message.message"),
                        data: this.systemLanguages.reduce((acc, lang) => {
                            acc[lang] = _.get(this.welcomeMessage.languageContent, `[${lang}].content`, "");
                            return acc;
                        }, {}),
                        validation: "required",
                    },
                ];
            },
            set(v) {
                this.welcomeMessage.languageContent = this.systemLanguages.reduce((acc, lang) => {
                    acc[lang] = {};
                    for (const _v of v) acc[lang][_v.field] = _v.data[lang];
                    acc[lang]["name"] = this.selectedWMType;
                    return acc;
                }, {});
            },
        },
    },
    methods: {
        ...mapActions({
            getWelcomeMessages: "welcomeMessage/getWelcomeMessages",
            getWelcomeMessage: "welcomeMessage/getWelcomeMessage",
            createWelcomeMessage: "welcomeMessage/createWelcomeMessage",
            updateWelcomeMessage: "welcomeMessage/updateWelcomeMessage",
            deleteWelcomeMessage: "welcomeMessage/deleteWelcomeMessage",
        }),
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
            this.reset();
        },
        showModalWithoutEdit() {
            this.isModalShow = true;
            this.editMode = false;
        },
        domDecoder(str, item) {
            if (str == undefined) {
                str = Object.keys(item.languageContent).map((key) => {
                    this.selectedLang = key;
                    return item.languageContent[key];
                })[0].content;
            }
            let parser = new DOMParser();
            let dom = parser.parseFromString("<!doctype html><body>" + str, "text/html");
            return dom.body.textContent;
        },
        read(obj, key) {
            return (
                _.get(obj, key) ||
                _.get(obj, "languageContent." + Object.keys(obj.languageContent)[0] + ".name") ||
                _.get(obj, "languageContent." + this.selectedLang + ".name")
            );
        },
        getFilteredRows() {
            const filteredRows = this.searchText
                ? this.undeleted.filter((x) => {
                      const validLangs = Object.keys(x.languageContent);
                      const lang = validLangs.includes(this.$i18n.locale) ? this.$i18n.locale : validLangs[0];
                      return (
                          x.languageContent[lang].name
                              .toLocaleLowerCase("tr-TR")
                              .includes(this.searchText.toLocaleLowerCase("tr-TR")) ||
                          x.languageContent[lang].content
                              .toLocaleLowerCase("tr-TR")
                              .includes(this.searchText.toLocaleLowerCase("tr-TR"))
                      );
                  })
                : this.undeleted;

            return filteredRows;
        },
        onDeleteLanguage(data) {
            const newMessage = toObject2(data.content, "content");
            Object.keys(newMessage).map((langKey) => {
                return (newMessage[langKey].name = this.selectedWMType);
            });
            this.welcomeMessage.languageContent = newMessage;
        },
        onSearch(value) {
            log("onSearch", value);
            this.searchText = value;
        },

        get() {
            this.isBusy = true;

            this.getWelcomeMessages({
                perPage: this.paginationCursor.perPage,
                page: this.paginationCursor.page,
            }).then(() => {
                this.isBusy = false;
                if (this.queryAction == "create") this.isModalShow = true;
            });
        },
        edit(id) {
            this.getWelcomeMessage(id).then((response) => {
                this.editMode = true;
                this.welcomeMessage = response;
                this.selectedWMType = this.welcomeMessage.languageContent[this.languages[0].code]
                    ? this.welcomeMessage.languageContent[this.languages[0].code].name
                    : null;
                /*this.$bvModal.show("welcome-message-form");*/
                this.showModal();
            });
        },
        remove(id) {
            this.$dialog
                .confirm(this.$t("message.deleteWelcomeMessageQuestion"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.getWelcomeMessage(id).then((welcomeMessage) => {
                        log("deleting welcome message", welcomeMessage);
                        this.deleteWelcomeMessage(id).then((x) => {
                            const socketMessageId = this.isAdmin
                                ? "admin welcome-message-deleted"
                                : "user welcome-message-deleted";
                            Object.keys(welcomeMessage.languageContent).forEach((lang) => {
                                sendSocketMessage(socketMessageId, {
                                    id,
                                    type: _.get(welcomeMessage, `languageContent.${lang}.name`),
                                    lang: lang,
                                    siteId: this.site._id,
                                });
                            });

                            this.$toasted.show(this.$t("message.welcomeMessageDeleted"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                        });
                    });
                });
        },
        reset() {
            this.welcomeMessage = _.cloneDeep(welcomeMessageInitialObject);
            this.submitClicked = false;
            this.selectedWMType = null;
        },
        handleSave() {
            this.submitClicked = true;
            this.$validator.validateAll().then((response) => {
                if (response) {
                    this.$refs.MultiContent.validate().then((isMultiContentValid) => {
                        if (isMultiContentValid) {
                            this.loading = true;
                            log("welcomeMessage ", this.welcomeMessage);
                            const result = this.welcomeMessage._id
                                ? this.updateWelcomeMessage(this.welcomeMessage)
                                : this.createWelcomeMessage(this.welcomeMessage);

                            result
                                .then((response) => {
                                    this.$toasted.show(this.$t("message.welcomeMessageSaved"), {
                                        duration: 3000,
                                        position: "bottom-center",
                                    });

                                    log("welcome message  response -> ", response);
                                    let socketMessageId;
                                    if (!this.welcomeMessage._id)
                                        socketMessageId = this.isAdmin
                                            ? "admin welcome-message-added"
                                            : "user welcome-message-added";
                                    else
                                        socketMessageId = this.isAdmin
                                            ? "admin welcome-message-updated"
                                            : "user welcome-message-updated";
                                    sendSocketMessage(socketMessageId, {
                                        id: response.id,
                                        type:
                                            _.get(response, "languageContent.en.name") ||
                                            _.get(response, "languageContent.tr.name"),
                                        contents: response.languageContent,
                                        siteId: this.site._id,
                                    });

                                    /*this.$bvModal.hide("welcome-message-form");*/
                                    this.reset();
                                    this.get();
                                })
                                .finally(() => {
                                    this.loading = false;
                                    this.submitClicked = false;
                                    this.hideModal();
                                });
                        }
                    });
                } else {
                    this.loading = false;
                }
            });
        },
    },
    mounted() {
        this.get();
        log("default lang is", this.defaultLang);
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
    watch: {
        paginationCursor: {
            deep: true,
            handler() {
                this.get();
            },
        },
        selectedWMType(val) {
            this.welcomeMessage.languageContent = Object.keys(this.welcomeMessage.languageContent).reduce((a, b) => {
                a[b] = {
                    name: val,
                    content: this.welcomeMessage.languageContent[b].content,
                };
                return a;
            }, {});
        },
    },
};
</script>

<style lang="scss" scoped>
.new-theme {
    #welcome-messages {
        .actionButton {
            border: none;
            box-shadow: none;
        }
    }
}

.fab {
    width: 50px;
    height: 50px;
    border-radius: 25px;
    background-color: white;
    position: absolute;
    bottom: 2%;
    left: 16%;
    z-index: 9999;
    transition: all 0.3s;
    box-shadow: 0 0 0 0 rgba(100, 100, 100, 1);
    transform: scale(1);
    animation: pulse 3s infinite;
    cursor: pointer;
}

@keyframes pulse {
    0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(100, 100, 100, 0.5);
    }

    70% {
        transform: scale(1);
        box-shadow: 0 0 0 10px #00000000;
    }

    100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 #00000000;
    }
}

#buttons {
    display: flex;
    justify-content: flex-end;
    gap: 7px;
}
</style>
<style lang="scss">
#welcome-message-form {
    .ant-modal {
        width: 670px !important;
    }
}
</style>
